import {
  buildWorkspacePath,
  DEFAULT_WORKSPACE_ID,
  isOrganizationGlobalPath,
  ORGANIZATION_ROUTE_ROOTS,
  parseLegacyOrganizationRoute,
  parseWorkspaceRoute,
} from "@/utils/workspaceRoutes"

const GLOBAL_ROUTE_ROOTS = new Set([
  "agent-training",
  "getting-started",
  "integrations",
  "plans",
  "portal-redirect",
  "reactivate",
  "select-org",
  "shared",
  "signin",
  "signup",
  "signup-welcome",
  "sso-callback",
])

const isAdminRoute = (path: string): boolean =>
  path === "/admin" || path.startsWith("/admin/")

export function splitPathQueryHash(href: string): {
  path: string
  query: string
  hash: string
} {
  const hashIndex = href.indexOf("#")
  const hash = hashIndex === -1 ? "" : href.slice(hashIndex)
  const withoutHash = hashIndex === -1 ? href : href.slice(0, hashIndex)
  const queryIndex = withoutHash.indexOf("?")

  if (queryIndex === -1) {
    return { path: withoutHash, query: "", hash }
  }

  return {
    path: withoutHash.slice(0, queryIndex),
    query: withoutHash.slice(queryIndex + 1),
    hash,
  }
}

export function getCurrentCanonicalPath(asPath?: string): string | null {
  if (!asPath) {
    return null
  }

  const { path } = splitPathQueryHash(asPath)
  return parseWorkspaceRoute(path) ? path : null
}

function getFirstPathSegment(path: string): string | null {
  const [firstSegment] = path.replace(/^\/+/, "").split("/")
  return firstSegment || null
}

function cleanQueryParams(params: URLSearchParams): URLSearchParams {
  const cleaned = new URLSearchParams()
  for (const [key, value] of params.entries()) {
    if (value && value.trim() !== "") {
      cleaned.set(key, value)
    }
  }
  return cleaned
}

export function createQueryHref(
  path: string,
  params: URLSearchParams,
  hash = "",
): string {
  const searchString = cleanQueryParams(params).toString()
  return `${path}${searchString ? `?${searchString}` : ""}${hash}`
}

export function getCanonicalDestinationPath(
  path: string,
  orgSlug: string | undefined,
  currentCanonicalRoute: ReturnType<typeof parseWorkspaceRoute>,
): string {
  const cleanPath = path.replace("/[slug]", "")
  const explicitRoute = parseWorkspaceRoute(cleanPath)
  if (explicitRoute) {
    if (isAdminRoute(explicitRoute.relativePath)) {
      return explicitRoute.relativePath
    }
    return isOrganizationGlobalPath(explicitRoute.relativePath)
      ? `/${explicitRoute.organizationSlug}${explicitRoute.relativePath}`
      : cleanPath
  }

  const firstSegment = getFirstPathSegment(cleanPath)
  if (firstSegment && GLOBAL_ROUTE_ROOTS.has(firstSegment)) {
    return cleanPath
  }

  const legacyRoute = parseLegacyOrganizationRoute(cleanPath)
  if (legacyRoute && isAdminRoute(legacyRoute.relativePath)) {
    return legacyRoute.relativePath
  }

  if (isAdminRoute(cleanPath)) {
    return cleanPath
  }

  if (legacyRoute && isOrganizationGlobalPath(legacyRoute.relativePath)) {
    return cleanPath
  }

  if (isOrganizationGlobalPath(cleanPath)) {
    const organizationSlug =
      orgSlug ?? currentCanonicalRoute?.organizationSlug ?? null
    return organizationSlug ? `/${organizationSlug}${cleanPath}` : cleanPath
  }

  if (firstSegment && ORGANIZATION_ROUTE_ROOTS.has(firstSegment)) {
    if (!currentCanonicalRoute) {
      return orgSlug ? `/${orgSlug}${cleanPath}` : cleanPath
    }
    const organizationSlug = orgSlug ?? currentCanonicalRoute.organizationSlug
    const workspaceId =
      organizationSlug === currentCanonicalRoute.organizationSlug
        ? currentCanonicalRoute.workspaceId
        : DEFAULT_WORKSPACE_ID
    return buildWorkspacePath(organizationSlug, workspaceId, cleanPath)
  }

  if (legacyRoute) {
    const workspaceId =
      legacyRoute.organizationSlug === currentCanonicalRoute?.organizationSlug
        ? currentCanonicalRoute?.workspaceId
        : DEFAULT_WORKSPACE_ID
    return buildWorkspacePath(
      legacyRoute.organizationSlug,
      workspaceId,
      legacyRoute.relativePath,
    )
  }

  if (!currentCanonicalRoute) {
    return orgSlug ? `/${orgSlug}${cleanPath}` : cleanPath
  }

  if (orgSlug) {
    const workspaceId =
      orgSlug === currentCanonicalRoute.organizationSlug
        ? currentCanonicalRoute.workspaceId
        : DEFAULT_WORKSPACE_ID
    return buildWorkspacePath(orgSlug, workspaceId, cleanPath)
  }

  return cleanPath
}
